import { selectAppUser } from '@/lib/redux/app.selectors';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGetRootAccountInfo } from './useGetRootAccountInfo';

export interface RolePermissions {
  // Role level flags (inclusive of lower levels)
  isUser: boolean; // USER or higher
  isAdmin: boolean; // ADMIN-USER or higher
  isAdminNIR: boolean; // Admin Not In Root (ANIR) or higher
  isSuper: boolean; // SUPER-USER only

  // User info
  userType: 'USER' | 'ADMIN-USER' | 'SUPER-USER' | 'IDENTITY-USER' | null;
}

interface UseRolePermissionsOptions {
  accountId?: string;
}

/**
 * Hook to determine user role permissions for account-related features.
 *
 * Permission levels (inclusive of lower levels):
 * - USER: Base level, can view own data
 * - ADMIN-USER: Can see settings as read-only
 * - ANIR (Admin Not In Root): ADMIN-USER viewing a sub-account - can edit most settings
 * - SUPER-USER: Can edit everything including super-user-only fields
 *
 * @param options.accountId - Optional account ID to check ANIR permissions against
 */
export const useRolePermissions = (
  options: UseRolePermissionsOptions = {}
): RolePermissions => {
  const { accountId } = options;
  const appUser = useSelector(selectAppUser);

  // Use the new root account info hook to determine if user is viewing a sub-account
  const { rootAccountId, isRootAccount } = useGetRootAccountInfo({ accountId });

  const permissions = useMemo(() => {
    const userType = appUser?.user_type || null;

    // Base permission flags
    const isSuper = userType === 'SUPER-USER';
    const isAdmin = userType === 'ADMIN-USER' || isSuper;
    const isUser = !!userType; // Any authenticated user

    // ANIR check: Admin user viewing a sub-account (not their root account)
    // If the accountId exists, has a root account, and is not itself the root account
    const isAdminNotInRoot = isAdmin && !!accountId && !!rootAccountId && !isRootAccount;

    // Role level flags (inclusive of lower levels)
    const isAdminNIR = isAdminNotInRoot || isSuper;

    return {
      isUser,
      isAdmin,
      isAdminNIR,
      isSuper,
      userType,
    };
  }, [appUser, accountId, rootAccountId, isRootAccount]);

  return permissions;
};
